import { Container, Point } from 'pixi.js';

import { addToUI } from 'game/core/Playable2DRootContainer';
import { getScreenSize } from 'game/core/screen';
import { GameEvent } from 'game/events/gameEvent';
import { EventName } from 'game/events/eventsName';

import gsap, { Power1 } from 'gsap';
import { MonoBehavior } from 'game/core/monoBehavior';
import { Layout } from 'game/LayoutManager';
import { UIBackground } from './UIBackground';
import { MENU_TRANSITION_TIME } from 'constants/move';
import { GAEvent, GAEventNames } from 'game/analytics/events';
import { StatisticArea } from './StatisticArea';
import { DESKTOP_UI_WIDTH, Displays, DESKTOP_SIDEBAR_WIDTH } from 'constants/layout';
import { GameSettings } from './GameSettings';
import { MenuButtons } from './MenuButtons';
import { GameDesigns } from './GameDesigns';

export class GameUI extends MonoBehavior {
    statisticArea: StatisticArea;
    uiContainer: Container;
    contentContainer: Container;

    uiBackground: UIBackground;
    gameSettings: GameSettings;
    gameDesigns: GameDesigns;

    isMenuVisible: boolean;
    menuButtons: MenuButtons;
    areDesignVisible = false;
    areSettingsVisible = false;
    constructor() {
        super();
        this.showSettings = this.showSettings.bind(this);
        this.showDesign = this.showDesign.bind(this);
        this.hideOptions = this.hideOptions.bind(this);
        GameEvent.add(EventName.FADE_UI, this.fadeUI.bind(this));

        this.uiContainer = new Container();
        this.uiContainer.sortableChildren = true;

        this.contentContainer = new Container();
        this.contentContainer.sortableChildren = true;

        this.gameSettings = new GameSettings(this.hideOptions);
        this.gameDesigns = new GameDesigns(this.hideOptions);

        this.statisticArea = new StatisticArea(this.contentContainer);

        this.uiBackground = new UIBackground(this.uiContainer);

        this.isMenuVisible = false;

        this.uiContainer.addChild(this.contentContainer);

        // move to settings container

        this.menuButtons = new MenuButtons(this);
        this.initializeUiPosition();
    }
    fadeUI({ alpha, duration }: { alpha: number; duration: number }) {
        gsap.to(this.uiContainer, { alpha, duration: duration });
    }
    handleOptions(width: number, height: number, onResize = false) {
        const easeTime = onResize ? 0 : MENU_TRANSITION_TIME;
        const device = Layout.getDisplay();
        const areOptionsVisible = this.areSettingsVisible || this.areDesignVisible;
        this.uiBackground.handleOptions(areOptionsVisible, onResize);

        switch (device) {
            case Displays.DESKTOP:
                this.isMenuVisible = true;

                if (areOptionsVisible) {
                    gsap.to(this.contentContainer, { alpha: 0, ease: Power1.easeInOut, duration: easeTime });
                    gsap.to(this.uiContainer.position, {
                        x: width - DESKTOP_UI_WIDTH,
                        y: 0,
                        ease: Power1.easeInOut,
                        duration: easeTime,
                    });
                }

                if (!areOptionsVisible) {
                    gsap.to(this.contentContainer, {
                        alpha: 1,
                        ease: Power1.easeInOut,
                        duration: easeTime,
                    });
                    gsap.to(this.uiContainer.position, {
                        x: width - DESKTOP_SIDEBAR_WIDTH,
                        y: 0,
                        ease: Power1.easeInOut,
                        duration: easeTime,
                    });
                }

                break;

            case Displays.TABLET:
                this.isMenuVisible = true;
                break;
            case Displays.MOBILE:
                break;
        }
        if (this.areDesignVisible) this.gameDesigns.show(easeTime);
        else this.gameDesigns.hide(easeTime);

        if (this.areSettingsVisible) this.gameSettings.show(easeTime);
        else this.gameSettings.hide(easeTime);

        this.gameSettings.handleOptions(height);
        this.menuButtons.handleOptions(onResize, areOptionsVisible);
        this.gameDesigns.handleOptions();
    }
    earlyResize(): void {
        this.hideOptions();
    }
    resize(width: number, height: number) {
        this.handleOptions(width, height, true);
        const device = Layout.getDisplay();

        switch (device) {
            case Displays.DESKTOP:
                this.uiContainer.position.set(width - DESKTOP_SIDEBAR_WIDTH, 0);

                break;
            case Displays.TABLET:
                this.uiContainer.position.set(0, 0);
                break;
            case Displays.MOBILE:
                this.uiContainer.position.set(0, 0);

                break;
        }
    }

    showSettings() {
        if (!this.areSettingsVisible) {
            GAEvent(GAEventNames.GAME_SETTINGS_OPEN, {});

            GameEvent.emit(EventName.OPEN_SETTINGS);
            const { width, height } = getScreenSize();
            this.areDesignVisible = false;
            this.areSettingsVisible = true;
            this.handleOptions(width, height);
            document.body.style.overflow = 'auto';
        }
    }
    showDesign() {
        if (!this.areDesignVisible) {
            GAEvent(GAEventNames.GAME_DESIGN_OPEN, {});
            GameEvent.emit(EventName.OPEN_SETTINGS);
            const { width, height } = getScreenSize();
            this.areSettingsVisible = false;
            this.areDesignVisible = true;
            this.handleOptions(width, height);
            document.body.style.overflow = 'hidden';
        }
    }

    hideOptions() {
        if (this.areSettingsVisible || this.areDesignVisible) {
            GameEvent.emit(EventName.HIDE_SETTINGS);

            const { width, height } = getScreenSize();
            this.areSettingsVisible = false;
            this.areDesignVisible = false;
            this.isMenuVisible = false;
            this.handleOptions(width, height);
        }
        this.menuButtons.showButtons();
        document.body.style.overflow = 'auto';
    }

    initializeUiPosition() {
        addToUI(this.uiContainer);
        this.uiContainer.position.set(getScreenSize().width - DESKTOP_SIDEBAR_WIDTH, 0);
    }

    start(): void {
        const { width, height } = getScreenSize();
        this.resize(width, height);
    }
}
